/*
 * delivery-scheduler.js
 * ---------------------------------------------------------------------------
 * Custom fulfilment scheduler for the Horizon cart page.
 * Pairs with blocks/delivery-scheduler.liquid.
 *
 * Responsibilities:
 *   - Pickup vs. local-delivery toggle
 *   - District -> zone mapping (no postal codes)
 *   - Delivery date + time-slot pickers
 *   - Free-shipping logic (subtotal threshold OR bottle count)
 *   - Zone delivery fee, collected via hidden "fee" products (line items)
 *   - Discovery Bay minimum-order enforcement (client-side)
 *   - Per-product daily-availability warnings
 *
 * State of record = cart attributes, so selections survive Horizon's cart
 * morph re-renders. The component re-hydrates on every `cart:update`.
 *
 * NOTE: this is client-side only. A determined customer can bypass the
 * Discovery Bay / slot checks by going straight to /checkout. See the setup
 * guide for the limits of a no-app build.
 * ---------------------------------------------------------------------------
 */

const ATTR = {
  method:    'delivery_method',          // store_pickup | local_delivery
  pickup:    'pickup_location',           // only written for Store Pickup
  district:  'delivery_district',         // e.g. Yau Ma Tei
  zone:      'delivery_zone',             // e.g. Kowloon & New Territories
  date:      'delivery_date',             // YYYY-MM-DD
  slot:      'delivery_time_slot',        // e.g. 14:00 – 17:00
  fee:       'delivery_fee_amount',       // numeric string e.g. "150" — "0" for free/pickup
  completed: 'delivery_setup_completed',  // "true" | "false"
};

// Map between internal JS radio-button values and the cart-attribute values.
// Radio buttons keep their original labels (Pickup / Local Delivery) so the
// existing JS logic, CSS selectors, and Liquid comparisons that rely on them
// stay unchanged.  Conversion only happens when reading / writing attributes.
const METHOD_TO_ATTR   = { 'Pickup': 'store_pickup', 'Local Delivery': 'local_delivery' };
const METHOD_FROM_ATTR = { 'store_pickup': 'Pickup',  'local_delivery': 'Local Delivery' };

const ZONE_KEYS = ['island', 'kowloon', 'nt', 'db'];

/** POST helper for the Shopify cart AJAX API. */
async function cartPost(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

/** Extract the trailing numeric ID from whatever the merchant pasted. */
function numericId(value) {
  const matches = String(value || '').match(/\d+/g);
  return matches && matches.length ? matches[matches.length - 1] : '';
}

/** Local YYYY-MM-DD (no timezone shift). */
function isoDate(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Today's calendar date in Hong Kong, returned as a JS Date set to local
 * midnight on that same calendar day. Lets the date list always reflect HK
 * regardless of where the customer (or tester) is browsing from.
 */
function todayInHK() {
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Hong_Kong',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const [y, m, d] = fmt.format(new Date()).split('-').map(Number);
    return new Date(y, m - 1, d);
  } catch (e) {
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }
}

/**
 * Current hour (0–23) in Hong Kong — used for the same-day order cutoff.
 */
function hourInHK() {
  try {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Hong_Kong',
      hour: 'numeric',
      hourCycle: 'h23',
    });
    return Number(fmt.format(new Date()));
  } catch (e) {
    return new Date().getHours();
  }
}

/** Shopify route, with a hard-coded fallback. */
function route(name, fallback) {
  return (typeof window !== 'undefined' && window.Theme && window.Theme.routes && window.Theme.routes[name]) || fallback;
}

class DeliveryScheduler extends HTMLElement {
  #busy = false;          // true while we are writing to the cart
  #pendingWrite = false;  // a user change arrived while we were busy
  #pendingHydrate = false; // an external cart update arrived while we were busy
  #checkoutAllowed = false;
  #feeRowKey = '';        // cart line key of the current hidden fee item, if any
  #loaderLocks = new Set(); // keys held by active loader-callers
  #cartDisabledObserver = null; // watches cart-items-component for disable class
  #boundCartUpdate;
  #boundCheckoutGuard;
  #boundFeeLinkGuard;
  #boundPageShow;
  #boundVisibility;

  connectedCallback() {
    // Parse static configuration (zones, fees, slots — set in the theme editor).
    this.config = this.#readJSON('[data-delivery-config]') || {};
    if (!this.config || !this.config.zoneFees) return; // misconfigured block

    this.districts = this.#parseDistricts(this.config.districtsRaw);
    // Two-step area picker: group districts by zone. Zones with exactly one
    // district (Discovery Bay, Ma Wan, Park Island) become their own direct
    // selectable "area" with no second step; zones with several districts
    // (HK Island, Kowloon, New Territories) show a sub-district step.
    this.mainZones = this.#buildMainZones();
    this.districtDays = this.#parseDistrictDays(this.config.districtDaysRaw);
    this.blackoutDates = this.#parseBlackoutDates(this.config.blackoutDatesRaw);
    this.slots = this.#splitLines(this.config.timeSlotsRaw);
    this.pickupLocations = this.#splitLines(this.config.pickupLocationsRaw);
    this.dates = this.#buildDates();

    // Maps that depend on product metafields (built from the Liquid-rendered
    // cart snapshot; default to 1 bottle / no blackout when unknown).
    this.bottleMap = {};
    this.unavailMap = {};
    this.#refreshMetafieldMaps();

    this.#renderControls();

    // Delegated listeners on the host element survive child re-renders (morph).
    this.addEventListener('change', this.#onChange);

    // Re-hydrate whenever the cart changes anywhere on the page.
    this.#boundCartUpdate = this.#onCartUpdate.bind(this);
    document.addEventListener('cart:update', this.#boundCartUpdate);

    // --- Stale-page guards (Back/Forward cache + tab switching) -----------
    // The server cart can change while this page is frozen: the customer
    // opens a product in another tab and adds items, or navigates away, adds
    // items, and returns with the browser Back button (which restores this
    // page from the back/forward cache WITHOUT re-running any of our code).
    // In both cases the page still shows — and would check out with — an
    // outdated fee. Re-hydrating on pageshow(persisted)/tab-return re-runs
    // the normal evaluate cycle against the live cart; the existing fee
    // "mismatch" reconciliation then corrects the fee line automatically.
    // (Real bug: order #W1541 — fee added at 6 bottles stayed after the
    // customer reached 12 via Back-button navigation.)
    this.#boundPageShow = this.#onPageShow.bind(this);
    window.addEventListener('pageshow', this.#boundPageShow);
    this.#boundVisibility = this.#onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.#boundVisibility);

    // Capture-phase guard on the checkout button(s).
    this.#boundCheckoutGuard = this.#onCheckoutClick.bind(this);
    document.addEventListener('click', this.#boundCheckoutGuard, true);

    // Capture-phase guard so the hidden "Delivery Fee" cart line can't be
    // opened as if it were a real product — covers keyboard activation too,
    // since the CSS pointer-events:none only stops mouse/touch.
    this.#boundFeeLinkGuard = this.#onFeeLinkClick.bind(this);
    document.addEventListener('click', this.#boundFeeLinkGuard, true);

    // Inject the full-page loader overlay if it isn't on the page yet
    // (singleton — only one loader regardless of how many delivery-scheduler
    // elements are connected during a morph cycle).
    if (!document.getElementById('ds-cart-loader')) {
      const loader = document.createElement('div');
      loader.id = 'ds-cart-loader';
      loader.setAttribute('hidden', '');
      loader.setAttribute('aria-live', 'polite');
      loader.setAttribute('role', 'status');
      loader.innerHTML =
        '<div class="ds-loader__spinner" aria-hidden="true"></div>' +
        '<p class="ds-loader__text">Please wait, cart is updating\u2026</p>';
      document.body.appendChild(loader);
    }

    // Watch for cart-items-component being disabled (Horizon adds/removes the
    // 'cart-items-disabled' class while a quantity update is in flight).
    // We hook that to show/hide our loader so the user can't interact while
    // Shopify processes the change — even for updates not triggered by us.
    const cartComp = document.querySelector('cart-items-component');
    if (cartComp) {
      this.#cartDisabledObserver = new MutationObserver(() => {
        if (cartComp.classList.contains('cart-items-disabled')) {
          this.#showLoader('external');
        } else {
          this.#hideLoader('external');
        }
      });
      this.#cartDisabledObserver.observe(cartComp, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // First hydration from the live cart.
    this.#hydrate();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#boundCartUpdate);
    window.removeEventListener('pageshow', this.#boundPageShow);
    document.removeEventListener('visibilitychange', this.#boundVisibility);
    document.removeEventListener('click', this.#boundCheckoutGuard, true);
    document.removeEventListener('click', this.#boundFeeLinkGuard, true);
    this.#cartDisabledObserver?.disconnect();
    this.#cartDisabledObserver = null;
    // Always clean up the loader when we disconnect so we don't leave a
    // frozen overlay on screen if the element is removed mid-write.
    this.#loaderLocks.clear();
    this.#updateLoader();
  }

  /* ----------------------------------------------------------------------- */
  /* Parsing helpers                                                          */
  /* ----------------------------------------------------------------------- */

  #readJSON(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.warn('[delivery-scheduler] bad JSON in', selector, e);
      return null;
    }
  }

  #splitLines(raw) {
    return String(raw || '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
  }

  #parseDistricts(raw) {
    return this.#splitLines(raw)
      .map((line) => {
        const [name, zone] = line.split('|').map((p) => p.trim());
        return { name, zone: (zone || '').toLowerCase() };
      })
      .filter((d) => d.name && ZONE_KEYS.includes(d.zone));
  }

  /**
   * Group parsed districts by zone into the "main area" list shown as the
   * first step of the delivery-area picker.
   *  - Zones with a single district (Discovery Bay, Ma Wan, Park Island)
   *    become a leaf entry: picking the area IS picking the district.
   *  - Zones with several districts (HK Island, Kowloon, New Territories)
   *    become a group entry with an alphabetically-sorted sub-district list.
   */
  #buildMainZones() {
    // "db"'s configured zone label ("Discovery Bay / Ma Wan / Park Island") is
    // the right wording for the delivery-fee summary and the saved cart
    // attribute, but too long for a dropdown option — show the short form
    // there instead. Every other zone uses its normal configured label.
    const MAIN_ZONE_SHORT_LABELS = { db: 'DB / Ma Wan / Park Island' };
    const groups = {};
    for (const d of this.districts) {
      (groups[d.zone] ||= []).push(d);
    }
    const zones = [];
    for (const key of ZONE_KEYS) {
      const list = groups[key];
      if (!list || list.length === 0) continue;
      // Every area is a two-step pick: choose the area, then the specific
      // district within it (e.g. Discovery Bay → Discovery Bay / Ma Wan /
      // Park Island). This also covers the (currently theoretical) case of
      // an area with just one configured district.
      const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
      zones.push({
        value: key,
        label: MAIN_ZONE_SHORT_LABELS[key] || this.config.zoneLabels[key],
        zone: key,
        districts: sorted,
      });
    }
    return zones;
  }

  /** Write the resolved district name into the hidden source-of-truth field. */
  #setDistrictValue(name) {
    const hidden = this.querySelector('[data-ds-district]');
    if (!hidden) return;
    hidden.value = name || '';
    // Bubble a change event so the existing [data-ds-district] handling
    // (date refresh, fee recalculation, cart attribute write) runs unchanged.
    hidden.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Populate and reveal the District step once an area has been chosen. */
  #onMainZoneChange(value) {
    const zoneInfo = this.mainZones.find((z) => z.value === value);
    const subField = this.querySelector('[data-ds-subdistrict-field]');
    const subSel = this.querySelector('[data-ds-subdistrict]');
    if (!zoneInfo) {
      if (subField) {
        subField.hidden = true;
        subField.style.display = 'none';
      }
      if (subSel) subSel.innerHTML = '';
      this.#setDistrictValue('');
      return;
    }
    if (subSel) {
      subSel.innerHTML = '';
      subSel.appendChild(this.#opt('', 'Select your district…'));
      for (const d of zoneInfo.districts) subSel.appendChild(this.#opt(d.name, d.name));
    }
    if (subField) {
      subField.hidden = false;
      subField.style.display = '';
    }
    // Picking the area alone isn't a complete selection yet — wait for the
    // specific district within it.
    this.#setDistrictValue('');
  }

  /**
   * Reverse-map a stored district name back onto the two-step picker so a
   * saved cart attribute (or a re-render) restores the correct area + sub-district.
   */
  /**
   * Re-derive the area/district picker from a saved district name (e.g. after
   * a full state sync from the server).
   *
   * IMPORTANT: a blank `name` here does NOT necessarily mean "nothing is
   * selected". It also happens for one moment mid-selection — the instant a
   * new area is picked, its district is written to the server as "" (not
   * chosen yet). That write can trigger a fee-line change, which triggers a
   * full re-sync of every control from the server's response — and if that
   * resync arrives while the district still reads "", blanking the Area
   * field too would silently undo the area the customer just picked (Bug:
   * "select area → pick district → pick a different area" → area resets
   * to blank after the loader). So on blank we only clear the District step
   * and leave whichever Area is currently selected untouched. Area is only
   * ever cleared explicitly — by #resetMethodFields() (switching
   * Delivery/Pickup) or by the customer picking a different area themselves.
   */
  #syncDistrictPickerFromValue(name) {
    const mainSel = this.querySelector('[data-ds-mainzone]');
    const subField = this.querySelector('[data-ds-subdistrict-field]');
    const subSel = this.querySelector('[data-ds-subdistrict]');
    if (!mainSel) return;
    const district = name ? this.districts.find((d) => d.name === name) : null;
    if (!district) {
      // Leave Area as-is (see note above). If a sub-district select is
      // currently showing, just clear its value — the list itself (and
      // which area it belongs to) is left alone.
      if (subSel && subSel.value) subSel.value = '';
      return;
    }
    const zoneInfo = this.mainZones.find((z) => z.zone === district.zone);
    if (!zoneInfo) return;
    mainSel.value = zoneInfo.value;
    if (subSel) {
      subSel.innerHTML = '';
      subSel.appendChild(this.#opt('', 'Select your district…'));
      for (const d of zoneInfo.districts) subSel.appendChild(this.#opt(d.name, d.name));
      subSel.value = name;
    }
    if (subField) {
      subField.hidden = false;
      subField.style.display = '';
    }
  }

  /**
   * "Sai Kung | Tue,Thu,Sat" (one district per line) → map of
   * lower-cased district name → Set of allowed weekday numbers (0 = Sun).
   * Districts not listed have no restriction.
   */
  #parseDistrictDays(raw) {
    const DAY_NUM = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    const map = {};
    for (const line of this.#splitLines(raw)) {
      const [name, daysPart] = line.split('|').map((p) => p.trim());
      if (!name || !daysPart) continue;
      const days = new Set();
      for (const token of daysPart.split(',')) {
        const key = token.trim().slice(0, 3).toLowerCase();
        if (key in DAY_NUM) days.add(DAY_NUM[key]);
      }
      if (days.size > 0) map[name.toLowerCase()] = days;
    }
    return map;
  }

  /**
   * Blocked dates (public holidays etc.) → Set of "YYYY-MM-DD" strings.
   * Accepts one date per line and/or comma-separated.
   */
  #parseBlackoutDates(raw) {
    const out = new Set();
    for (const line of this.#splitLines(raw)) {
      for (const token of line.split(',')) {
        const t = token.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(t)) out.add(t);
      }
    }
    return out;
  }

  /** Build the list of bookable dates from lead time + enabled weekdays. */
  #buildDates() {
    const days = (this.config.deliveryDays || []).map(Number);
    let lead = Number(this.config.leadTimeDays) || 0;
    // Same-day order cutoff (HK time): orders placed before the cutoff hour
    // may pick today; orders placed after it start from tomorrow.
    if (this.config.enableSameDayCutoff) {
      const cutoff = Number(this.config.sameDayCutoffHour);
      if (!Number.isNaN(cutoff)) {
        lead = hourInHK() < cutoff ? 0 : 1;
      }
    }
    const span = Number(this.config.maxDaysAhead) || 21;
    const out = [];
    const cursor = todayInHK();
    cursor.setDate(cursor.getDate() + lead);
    for (let i = 0; i < span && out.length < 60; i++) {
      const blocked = this.blackoutDates && this.blackoutDates.has(isoDate(cursor));
      if (!blocked && (days.length === 0 || days.includes(cursor.getDay()))) {
        out.push({
          value: isoDate(cursor),
          dow: cursor.getDay(),
          label: cursor.toLocaleDateString(undefined, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
          }),
        });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }

  /** Refresh bottle-count / blackout maps from the Liquid cart snapshot. */
  #refreshMetafieldMaps() {
    const snapshot = this.#readJSON('[data-delivery-cart]');
    if (!snapshot || !Array.isArray(snapshot.items)) return;
    for (const item of snapshot.items) {
      const id = String(item.variantId);
      this.bottleMap[id] = Number(item.bottlesPerUnit) || 1;
      this.unavailMap[id] = Array.isArray(item.unavailableDates)
        ? item.unavailableDates.map(String)
        : [];
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Rendering                                                                */
  /* ----------------------------------------------------------------------- */

  #renderControls() {
    const setOptions = (sel, opts) => {
      if (!sel) return;
      const prior = sel.value;
      sel.innerHTML = '';
      for (const [value, label] of opts) {
        sel.appendChild(this.#opt(value, label));
      }
      // Restore the prior selection if it is still a valid option.
      if (prior && Array.from(sel.options).some((o) => o.value === prior)) {
        sel.value = prior;
      }
    };

    // Pickup locations
    const pickupOpts = this.pickupLocations.length > 1
      ? [['', 'Select a location…'], ...this.pickupLocations.map((l) => [l, l])]
      : this.pickupLocations.map((l) => [l, l]);
    setOptions(this.querySelector('[data-ds-pickup-location]'), pickupOpts);

    // Delivery area (2-step picker: main area, then sub-district for multi-district areas)
    const mainZoneOpts = [['', 'Select your area…'], ...this.mainZones.map((z) => [z.value, z.label])];
    setOptions(this.querySelector('[data-ds-mainzone]'), mainZoneOpts);
    this.#syncDistrictPickerFromValue(this.querySelector('[data-ds-district]')?.value || '');

    // Dates (filtered by the selected district's allowed weekdays, if any)
    this.#refreshDateOptions();

    // Time slots
    const slotOpts = [['', 'Select a slot…'], ...this.slots.map((sl) => [sl, sl])];
    setOptions(this.querySelector('[data-ds-slot]'), slotOpts);
  }

  #opt(value, label) {
    const o = document.createElement('option');
    o.value = value;
    o.textContent = label;
    return o;
  }

  /**
   * (Re)populate the date <select>, filtered by the currently selected
   * district's allowed weekdays (e.g. Sai Kung → Tue/Thu/Sat only).
   * Keeps the prior selection when it is still a valid option; otherwise the
   * value resets to "" and the customer must pick a new date.
   */
  #refreshDateOptions() {
    const sel = this.querySelector('[data-ds-date]');
    if (!sel) return;
    const method = this.querySelector('[data-ds-method]:checked')?.value || '';
    const district =
      method === 'Local Delivery'
        ? (this.querySelector('[data-ds-district]')?.value || '')
        : '';
    const allowed = this.districtDays[district.toLowerCase()];
    const list = allowed ? this.dates.filter((d) => allowed.has(d.dow)) : this.dates;
    const prior = sel.value;
    sel.innerHTML = '';
    sel.appendChild(this.#opt('', 'Select a date…'));
    for (const d of list) sel.appendChild(this.#opt(d.value, d.label));
    if (prior && Array.from(sel.options).some((o) => o.value === prior)) {
      sel.value = prior;
    }
  }

  /** Apply saved cart-attribute values back onto the controls. */
  #applySavedState(attrs) {
    const set = (sel, val) => {
      const el = this.querySelector(sel);
      if (el && val != null) el.value = val;
    };

    // Prefer the radio the user has already clicked over whatever the cart
    // attribute says.  A delayed #hydrate(false) (scheduled 60ms after an
    // external cart update) can arrive while a persist is still in-flight and
    // read a stale Delivery Method attribute that predates our own write.
    // Trusting the DOM state here prevents that stale read from hiding all
    // panels (updatePanels('')) over a selection the user just made.
    //
    // Cart attributes store method as "store_pickup"/"local_delivery".
    // Convert back to the internal radio-button values ("Pickup"/"Local Delivery").
    const checkedRadio = Array.from(this.querySelectorAll('[data-ds-method]')).find((r) => r.checked);
    const methodFromAttr = METHOD_FROM_ATTR[attrs[ATTR.method]] || '';
    // Default to Local Delivery when nothing is saved in cart attributes.
    // The Liquid already pre-checks the Local Delivery radio in this case, so
    // checkedRadio will normally already be set — this fallback is a safety net.
    const method = checkedRadio?.value || methodFromAttr || 'Local Delivery';

    this.querySelectorAll('[data-ds-method]').forEach((r) => {
      r.checked = r.value === method;
    });
    set('[data-ds-pickup-location]', attrs[ATTR.pickup] || (this.pickupLocations.length === 1 ? this.pickupLocations[0] : ''));
    set('[data-ds-district]', attrs[ATTR.district] || '');
    this.#syncDistrictPickerFromValue(attrs[ATTR.district] || '');
    // Rebuild the date list for this district before restoring the saved date
    // — a saved date that is no longer allowed simply won't restore.
    this.#refreshDateOptions();
    set('[data-ds-date]', attrs[ATTR.date] || '');
    set('[data-ds-slot]', attrs[ATTR.slot] || '');
    this.#updatePanels(method);
  }

  /** Show/hide the pickup vs. delivery panels, and the slot field (Pickup only). */
  #updatePanels(method) {
    const pickup = this.querySelector('[data-ds-panel="pickup"]');
    const delivery = this.querySelector('[data-ds-panel="delivery"]');
    const schedule = this.querySelector('[data-ds-schedule]');
    const slotField = this.querySelector('[data-ds-slot-field]');
    const subField = this.querySelector('[data-ds-subdistrict-field]');
    if (pickup) pickup.hidden = method !== 'Pickup';
    if (delivery) delivery.hidden = method !== 'Local Delivery';
    if (schedule) schedule.hidden = !method;
    // Slot is only shown for Store Pickup.
    // Use BOTH hidden attribute AND inline style because .delivery-scheduler__field
    // has display:flex (author CSS) which overrides the UA [hidden]{display:none}.
    if (slotField) {
      const hideSlot = method !== 'Pickup';
      slotField.hidden = hideSlot;
      slotField.style.display = hideSlot ? 'none' : '';
    }
    // District only applies to Local Delivery, and even then only once an
    // area has actually been picked (see #onMainZoneChange). Force it hidden
    // for every other method; leave Local Delivery's own state alone so a
    // district the customer already picked stays visible.
    if (subField && method !== 'Local Delivery') {
      subField.hidden = true;
      subField.style.display = 'none';
    }
  }

  /**
   * Clear every field whenever the fulfilment method is toggled. Pickup
   * location, district, date and time slot are all shown again under the
   * other method, but they mean something different there — without this,
   * a previously chosen zone/date/slot silently carried over (Bug 3).
   */
  #resetMethodFields() {
    const clear = (selector) => {
      const el = this.querySelector(selector);
      if (el) el.value = '';
    };
    clear('[data-ds-pickup-location]');
    clear('[data-ds-mainzone]');
    clear('[data-ds-subdistrict]');
    clear('[data-ds-district]');
    clear('[data-ds-date]');
    clear('[data-ds-slot]');
    const subField = this.querySelector('[data-ds-subdistrict-field]');
    if (subField) {
      subField.hidden = true;
      subField.style.display = 'none';
    }
    const notice = this.querySelector('[data-ds-notice]');
    if (notice) {
      notice.hidden = true;
      notice.innerHTML = '';
      delete notice.dataset.dsChecking;
    }
  }

  /**
   * Instant placeholder shown the moment a date is picked, before the live
   * cart has even been re-fetched — replaced by the real message (or
   * cleared) once #paint runs a moment later (Bug 2).
   */
  #showChecking() {
    const notice = this.querySelector('[data-ds-notice]');
    if (!notice) return;
    notice.dataset.dsChecking = '';
    notice.textContent = 'Checking availability for this date…';
    notice.hidden = false;
  }

  /**
   * Instantly de-emphasise the cart line for an outgoing delivery-fee
   * product, rather than leaving it at full price/opacity until the cart
   * API call + section morph confirm the removal a moment later (Bug 1).
   */
  #flagFeeRowRemoving(key) {
    if (!key || typeof CSS === 'undefined' || !CSS.escape) return;
    const row = document.querySelector(`.cart-items__table-row[data-key="${CSS.escape(key)}"]`);
    if (row) row.setAttribute('data-ds-fee-removing', '');
  }

  /**
   * Stamp a stable CSS class on the current fee cart line so the static
   * stylesheet can immediately hide its quantity controls and title link
   * without relying solely on the dynamic <style> key-selector block (which
   * only applies after a section morph).
   * Called from #paint on every state evaluation so it re-applies after any
   * cart update or section morph that re-renders the cart items table.
   */
  #markFeeRow() {
    // Clear any previously-marked row (zone may have changed, key rotated).
    document.querySelectorAll('.cart-items__table-row.ds-fee-row').forEach((r) => {
      r.classList.remove('ds-fee-row');
    });
    if (!this.#feeRowKey) return;
    if (typeof CSS === 'undefined' || !CSS.escape) return;
    const row = document.querySelector(
      `.cart-items__table-row[data-key="${CSS.escape(this.#feeRowKey)}"]`
    );
    if (row) row.classList.add('ds-fee-row');
  }

  /* ----------------------------------------------------------------------- */
  /* Reading current selections                                               */
  /* ----------------------------------------------------------------------- */

  #selections() {
    const checked = this.querySelector('[data-ds-method]:checked');
    return {
      method: checked ? checked.value : '',
      pickup: this.querySelector('[data-ds-pickup-location]')?.value || '',
      district: this.querySelector('[data-ds-district]')?.value || '',
      date: this.querySelector('[data-ds-date]')?.value || '',
      slot: this.querySelector('[data-ds-slot]')?.value || '',
    };
  }

  #zoneForDistrict(name) {
    const d = this.districts.find((x) => x.name === name);
    return d ? d.zone : '';
  }

  /** Variant IDs (numeric) used as delivery-fee line items. */
  #feeVariantIds() {
    const fv = this.config.feeVariants || {};
    return ZONE_KEYS.map((k) => numericId(fv[k])).filter(Boolean);
  }

  /* ----------------------------------------------------------------------- */
  /* Core evaluation                                                          */
  /* ----------------------------------------------------------------------- */

  /**
   * Inspect the live cart, compute fee + eligibility, update the UI, and
   * (when needed) reconcile the hidden fee line item.
   * @param {object} cart - parsed /cart.js response
   * @param {boolean} writeAttributes - persist selections (true on user input)
   */
  async #evaluate(cart, writeAttributes, options = {}) {
    const feeIds = this.#feeVariantIds();
    const sel = this.#selections();

    // Merchandise totals — exclude the hidden fee products. `realCount` is
    // the actual product quantity in the cart, which is NOT the same as
    // cart.item_count once a fee line exists: removing the last real bottle
    // still leaves item_count at 1 (the fee line itself), which previously
    // kept this whole block — and the fee charge — visible on what looked
    // like an empty cart (Bug 5).
    let merchandiseCents = 0;
    let bottles = 0;
    let realCount = 0;
    let feeLine = null;
    for (const item of cart.items || []) {
      const vid = String(item.variant_id);
      if (feeIds.includes(vid)) {
        feeLine = { key: item.key, variantId: vid, quantity: item.quantity };
        continue;
      }
      merchandiseCents += Number(item.final_line_price ?? item.line_price ?? 0);
      const perUnit = this.bottleMap[vid] || 1;
      bottles += perUnit * Number(item.quantity || 0);
      realCount += Number(item.quantity || 0);
    }

    const cartEmpty = realCount === 0;
    this.#feeRowKey = feeLine ? feeLine.key : '';

    // Free-shipping eligibility for standard zones (island / kowloon / nt).
    // DB / Ma Wan / Park Island use their own thresholds (dbMin*) below.
    const qualifiesFree =
      merchandiseCents >= Number(this.config.freeShipSubtotal) * 100 ||
      bottles >= Number(this.config.freeShipBottles);

    // Zone → fee calculation.
    // Rules:
    //   island / kowloon / nt  →  standard fee OR free (qualifiesFree)
    //   db                     →  $500 OR free (dbMin thresholds); never blocks checkout
    const zone = !cartEmpty && sel.method === 'Local Delivery' ? this.#zoneForDistrict(sel.district) : '';
    let feeDollars = 0;
    let dbInfoMessage = '';   // informational only — does NOT block checkout
    if (!cartEmpty && sel.method === 'Local Delivery' && zone) {
      const zoneFee = Number(this.config.zoneFees[zone]) || 0;
      if (zone === 'db') {
        const dbFree =
          bottles >= Number(this.config.dbMinBottles) ||
          merchandiseCents >= Number(this.config.dbMinSubtotal) * 100;
        feeDollars = dbFree ? 0 : zoneFee;
        if (!dbFree) {
          dbInfoMessage =
            `A ${this.config.currencySymbol || '$'}${zoneFee} delivery fee applies. ` +
            `Order ${this.config.dbMinBottles}+ bottles or ` +
            `${this.config.currencySymbol || '$'}${Number(this.config.dbMinSubtotal).toLocaleString()}+ for free delivery.`;
        }
      } else {
        // Standard zones: free if qualifies, otherwise apply zone fee
        feeDollars = qualifiesFree ? 0 : zoneFee;
      }
    }

    // Per-product daily availability.
    const unavailable = [];
    if (sel.date) {
      for (const item of cart.items || []) {
        const vid = String(item.variant_id);
        if (feeIds.includes(vid)) continue;
        if ((this.unavailMap[vid] || []).includes(sel.date)) {
          unavailable.push(item.product_title || item.title || 'An item');
        }
      }
    }

    // Target fee variant for the current zone. Never targets a fee variant
    // once the cart has no real merchandise left — this is what lets the
    // stray fee line actually get removed when the last bottle is taken out.
    const feesConfigured = feeIds.length > 0;
    let targetFeeVariant = '';
    if (!cartEmpty && sel.method === 'Local Delivery' && zone && feeDollars > 0) {
      targetFeeVariant = numericId((this.config.feeVariants || {})[zone]);
    }

    const state = {
      sel,
      zone,
      bottles,
      merchandiseCents,
      qualifiesFree,
      feeDollars,
      dbInfoMessage,
      unavailable,
      feeLine,
      targetFeeVariant,
      feesConfigured,
      cartEmpty,
    };

    this.#paint(state);

    // Reconcile the hidden fee line if it does not match the target.
    const mismatch =
      feesConfigured &&
      ((feeLine ? feeLine.variantId : '') !== targetFeeVariant ||
        (feeLine && feeLine.quantity !== 1));

    if (!this.#busy && (writeAttributes || mismatch)) {
      await this.#persist(state, options);
    }
  }

  /** Update summary text, notices, and checkout availability. */
  #paint(state) {
    const { sel, zone, bottles, merchandiseCents, feeDollars, dbInfoMessage, unavailable, cartEmpty } = state;
    const card = this.querySelector('[data-ds-card]');
    const summary = this.querySelector('[data-ds-summary]');
    const notice = this.querySelector('[data-ds-notice]');

    // When cart has no real merchandise, clear UI state and unblock checkout.
    // Card visibility itself is handled by CSS (.cart-page--empty rule) so we
    // never touch card.hidden here — that caused race conditions with the
    // Horizon section morph that fires after every quantity/fee change.
    if (cartEmpty) {
      if (summary) summary.hidden = true;
      if (notice) {
        notice.hidden = true;
        delete notice.dataset.dsChecking;
      }
      this.#checkoutAllowed = true;
      this.#reflectCheckoutState(true);
      return;
    }

    // Belt-and-suspenders: stamp ds-fee-row class on the current fee cart line
    // immediately so the static CSS rules hide its controls right away — without
    // waiting for the section to morph and re-render the dynamic <style> block.
    this.#markFeeRow();

    // Belt-and-suspenders: strip any stray `hidden` attribute that a morph or
    // unexpected code path may have applied.  The CSS display !important rules
    // are the real guard, but keeping the DOM clean helps screen-readers too.
    this.removeAttribute('hidden');
    if (card) card.removeAttribute('hidden');

    const cur = this.config.currencySymbol || '$';
    const money = (n) => `${cur}${Number(n).toLocaleString()}`;
    const dateLabel = this.querySelector('[data-ds-date-label]');
    if (dateLabel) dateLabel.textContent = sel.method === 'Pickup' ? 'Pickup date' : 'Delivery date';

    let valid = !!sel.method;

    // --- Summary line ---------------------------------------------------
    const parts = [];
    if (sel.method === 'Pickup') {
      if (sel.pickup) parts.push(`Pickup: ${sel.pickup}`);
      else valid = false;
      // Date required for Pickup
      if (sel.date) parts.push(sel.date);
      else valid = false;
      // Slot required for Pickup (if requireSlot is on)
      if (sel.slot) parts.push(sel.slot);
      else if (this.config.requireSlotForCheckout) valid = false;
    } else if (sel.method === 'Local Delivery') {
      if (zone) parts.push(`Deliver to ${this.config.zoneLabels[zone]}`);
      else valid = false;
      // Date required for Local Delivery; no slot required
      if (sel.date) parts.push(sel.date);
      else valid = false;
    }

    if (summary) {
      let feeText = '';
      if (sel.method === 'Local Delivery' && zone) {
        feeText =
          feeDollars > 0
            ? ` · Delivery fee: <strong>${money(feeDollars)}</strong>`
            : ' · Delivery: <strong>Free</strong>';
      }
      summary.classList.toggle('delivery-scheduler__summary--free', !!zone && feeDollars === 0 && sel.method === 'Local Delivery');
      summary.innerHTML = parts.length ? parts.join(' · ') + feeText : 'Select your options above to continue.';
      summary.hidden = false;
    }

    // --- Notices --------------------------------------------------------
    const messages = [];
    // DB / Ma Wan / Park Island: informational message only — does NOT block checkout
    if (dbInfoMessage) messages.push(dbInfoMessage);
    if (unavailable.length) {
      messages.push(
        `Not available on ${sel.date}: ${[...new Set(unavailable)].join(', ')}. Please choose another date.`
      );
      valid = false;
    }
    if (notice) {
      notice.innerHTML = messages.join('<br>');
      notice.hidden = messages.length === 0;
      delete notice.dataset.dsChecking;
    }

    this.#checkoutAllowed = valid;
    this.#reflectCheckoutState(valid);
  }

  /** Visually mark checkout buttons when delivery details are incomplete. */
  #reflectCheckoutState(allowed) {
    document.querySelectorAll('[name="checkout"]').forEach((btn) => {
      if (allowed) btn.removeAttribute('data-delivery-blocked');
      else btn.setAttribute('data-delivery-blocked', '');
    });
  }

  /* ----------------------------------------------------------------------- */
  /* Persistence                                                              */
  /* ----------------------------------------------------------------------- */

  #sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      ids.add(el.dataset.sectionId);
    });
    return Array.from(ids).join(',');
  }

  /** Write cart attributes + reconcile the fee line, then refresh the cart UI. */
  async #persist(state, options = {}) {
    // If a write is already running, remember to re-run once it finishes so
    // rapid consecutive selections are never dropped.
    if (this.#busy) {
      this.#pendingWrite = true;
      return;
    }
    this.#busy = true;
    let cartChanged = false;
    let result = null;
    const syncingNote = this.querySelector('[data-ds-syncing]');
    // Only show the full-page loader for writes that may take a visible amount
    // of time or change the cart total (fee add/remove, method/district change).
    // Date and slot selections are attribute-only writes — no loader needed.
    if (!options.suppressLoader) this.#showLoader('persist');
    try {
      const { sel, zone, feeDollars, targetFeeVariant, feeLine, feesConfigured, cartEmpty } = state;

      // 1. Adjust the hidden fee line item. Track whether any cart-modifying
      //    call happened so we can decide whether the cart UI needs to morph.
      if (feesConfigured) {
        const current = feeLine ? feeLine.variantId : '';
        if (current !== targetFeeVariant && syncingNote) {
          // Instant feedback (Bug 1) — shown before the network round trip,
          // not after, so the stale fee never sits there unexplained.
          syncingNote.hidden = false;
        }
        if (current && current !== targetFeeVariant) {
          this.#flagFeeRowRemoving(feeLine.key);
          await cartPost(route('cart_change_url', '/cart/change.js'), {
            id: feeLine.key,
            quantity: 0,
          });
          cartChanged = true;
        }
        if (targetFeeVariant && current !== targetFeeVariant) {
          await cartPost(route('cart_add_url', '/cart/add.js'), {
            items: [{ id: Number(targetFeeVariant), quantity: 1 }],
          });
          cartChanged = true;
        } else if (targetFeeVariant && feeLine && feeLine.quantity !== 1) {
          await cartPost(route('cart_change_url', '/cart/change.js'), {
            id: feeLine.key,
            quantity: 1,
          });
          cartChanged = true;
        }
      }

      // 2. Save attributes. Only ask Shopify to re-render the cart sections
      //    when the line items actually changed — otherwise a morph would
      //    needlessly wipe the customer's in-progress selections.
      // Once the cart has no real merchandise left, clear every DS attribute
      // instead of writing the current (now-meaningless) selections — so a
      // later, different order doesn't silently inherit old choices.
      const attributes = cartEmpty
        ? {
            [ATTR.method]:    '',
            [ATTR.pickup]:    '',
            [ATTR.district]:  '',
            [ATTR.zone]:      '',
            [ATTR.date]:      '',
            [ATTR.slot]:      '',
            [ATTR.fee]:       '',
            [ATTR.completed]: '',
          }
        : (() => {
            // Is this order "complete" — has the customer filled everything in?
            // This flag is read by Shopify Flow to flag/tag incomplete orders.
            const needSlot = this.config.requireSlotForCheckout;
            let complete = false;
            if (sel.method === 'Pickup') {
              complete = !!(sel.pickup && sel.date && (!needSlot || sel.slot));
            } else if (sel.method === 'Local Delivery') {
              // No slot required for Local Delivery
              complete = !!(sel.district && sel.date);
            }
            return {
              [ATTR.method]:    METHOD_TO_ATTR[sel.method] || '',
              [ATTR.pickup]:    sel.method === 'Pickup' ? sel.pickup : '',
              [ATTR.district]:  sel.method === 'Local Delivery' ? sel.district : '',
              [ATTR.zone]:      zone ? this.config.zoneLabels[zone] : '',
              [ATTR.date]:      sel.date,
              // Slot only relevant for Store Pickup
              [ATTR.slot]:      sel.method === 'Pickup' ? sel.slot : '',
              [ATTR.fee]:
                sel.method === 'Pickup'
                  ? '0'
                  : zone
                  ? String(feeDollars)
                  : '',
              [ATTR.completed]: complete ? 'true' : 'false',
            };
          })();

      const updateBody = { attributes };
      if (cartChanged) {
        updateBody.sections = this.#sectionIds();
        updateBody.sections_url = window.location.pathname;
      }
      result = await cartPost(route('cart_update_url', '/cart/update.js'), updateBody);

      // 3. Only dispatch cart:update (which triggers Horizon's section
      //    morph) when we actually touched the line items.
      if (cartChanged && result && result.sections) {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: result,
              data: { source: 'delivery-scheduler', sections: result.sections },
            },
          })
        );
      }
    } catch (e) {
      console.error('[delivery-scheduler] persist failed', e);
    } finally {
      this.#busy = false;
      if (syncingNote) syncingNote.hidden = true;
      if (!options.suppressLoader) this.#hideLoader('persist');
      if (this.#pendingWrite) {
        // A user change came in mid-write — re-evaluate with latest control values.
        this.#pendingWrite = false;
        this.#pendingHydrate = false;
        this.#hydrate(true);
      } else if (cartChanged) {
        // Re-render from the write's own response (avoids a racy re-fetch).
        this.#pendingHydrate = false;
        this.#hydrate(false, result);
      } else if (this.#pendingHydrate) {
        // An external cart update (quantity change, coupon, etc.) arrived while
        // we were busy persisting — re-evaluate now that we're free.
        this.#pendingHydrate = false;
        setTimeout(() => this.#hydrate(false), 60);
      }
      // else: pure attribute write, DOM intact, nothing to do.
    }
  }

  /* ----------------------------------------------------------------------- */
  /* Event handlers                                                           */
  /* ----------------------------------------------------------------------- */

  #onChange = (event) => {
    if (event.target.matches('[data-ds-mainzone]')) {
      this.#onMainZoneChange(event.target.value);
      return; // #setDistrictValue() bubbles its own change on [data-ds-district]
    }
    if (event.target.matches('[data-ds-subdistrict]')) {
      this.#setDistrictValue(event.target.value);
      return; // #setDistrictValue() bubbles its own change on [data-ds-district]
    }
    if (!event.target.matches('[data-ds-method], [data-ds-pickup-location], [data-ds-district], [data-ds-date], [data-ds-slot]')) {
      return;
    }
    if (event.target.matches('[data-ds-method]')) {
      this.#resetMethodFields();
      this.#updatePanels(event.target.value);
    }
    if (event.target.matches('[data-ds-method], [data-ds-district]')) {
      // District (or method) changed — the allowed dates may differ now.
      this.#refreshDateOptions();
    }
    if (event.target.matches('[data-ds-date]')) {
      this.#showChecking();
    }
    // Date and slot selections are lightweight attribute-only writes — no fee
    // line changes, no cart re-render.  Showing the full-page loader for these
    // is unnecessary friction, so we pass suppressLoader = true.
    const suppressLoader = event.target.matches('[data-ds-date], [data-ds-slot]');
    this.#hydrate(true, null, { suppressLoader });
  };

  /** Re-hydrate after any cart change made elsewhere on the page. */
  #onCartUpdate = (event) => {
    if (event?.detail?.data?.source === 'delivery-scheduler') return;
    if (this.#busy) {
      // We're mid-persist — remember to re-hydrate once we finish so the
      // external cart change (quantity update, coupon, etc.) isn't silently lost.
      this.#pendingHydrate = true;
      return;
    }
    setTimeout(() => this.#hydrate(false), 60);
  };

  /**
   * Fired when the page is shown again after a Back/Forward navigation.
   * `event.persisted` is true only when the browser restored the page from
   * its back/forward cache — i.e. our code did NOT re-run and everything on
   * screen (including the fee) is a frozen snapshot. Re-hydrate from the
   * live cart so the fee/threshold state is re-checked and reconciled.
   */
  #onPageShow = (event) => {
    if (!event.persisted) return; // normal load — connectedCallback already hydrated
    this.#staleRehydrate();
  };

  /**
   * Fired when the tab becomes visible again. If the customer changed the
   * cart in ANOTHER tab and switched back here, this page has no other way
   * of knowing — cart:update events don't cross tabs. Re-check on return.
   */
  #onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;
    this.#staleRehydrate();
  };

  /**
   * Shared re-hydrate for the two "this page may be stale" signals above.
   * Uses the same busy/pending pattern as #onCartUpdate so it can never
   * collide with an in-flight write. hydrate(false) re-reads the live cart
   * and re-runs #evaluate — whose existing fee-mismatch check performs the
   * actual correction, so no new fee logic is introduced here.
   */
  #staleRehydrate() {
    if (this.#busy) {
      this.#pendingHydrate = true;
      return;
    }
    setTimeout(() => this.#hydrate(false), 60);
  }

  /** Capture-phase guard: stop checkout while delivery details are invalid. */
  #onCheckoutClick = (event) => {
    const btn = event.target.closest && event.target.closest('[name="checkout"]');
    if (!btn) return;
    if (this.#checkoutAllowed) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const notice = this.querySelector('[data-ds-notice]');
    const card = this.querySelector('[data-ds-card]');
    if (notice && notice.hidden) {
      notice.textContent = 'Please complete your delivery or pickup details above before checking out.';
      notice.hidden = false;
    }
    (notice && !notice.hidden ? notice : card)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  /**
   * Capture-phase guard: the "Delivery Fee" line is a hidden charge, not a
   * real product — block its title/image link from opening the product
   * page, whether the click came from a mouse or from keyboard (Enter on a
   * focused link still fires a click event, which CSS pointer-events:none
   * alone does not stop).
   */
  #onFeeLinkClick = (event) => {
    if (!this.#feeRowKey) return;
    const link = event.target.closest && event.target.closest('a');
    if (!link) return;
    const row = link.closest('.cart-items__table-row[data-key]');
    if (row && row.dataset.key === this.#feeRowKey) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  /* ----------------------------------------------------------------------- */
  /* Full-page loader                                                          */
  /* ----------------------------------------------------------------------- */

  /**
   * Acquire a named loader lock. The overlay stays visible as long as at
   * least one lock is held — so simultaneous our-write + external-update
   * periods don't hide the overlay too early.
   * @param {'persist'|'external'} key
   */
  #showLoader(key) {
    this.#loaderLocks.add(key);
    this.#updateLoader();
  }

  /**
   * Release a named loader lock.
   * @param {'persist'|'external'} key
   */
  #hideLoader(key) {
    this.#loaderLocks.delete(key);
    this.#updateLoader();
  }

  #updateLoader() {
    const loader = document.getElementById('ds-cart-loader');
    if (!loader) return;
    loader.hidden = this.#loaderLocks.size === 0;
  }

  /* ----------------------------------------------------------------------- */
  /* Hydration                                                                */
  /* ----------------------------------------------------------------------- */

  /**
   * Run a full evaluation against the live cart.
   * @param {boolean} writeAttributes - persist selections (true on user input)
   * @param {object|null} presetCart - reuse an already-known cart object
   *   (e.g. the response of a write we just made) instead of re-fetching.
   *   Re-fetching /cart.js immediately after our own write occasionally
   *   raced with Shopify's cart API and returned a snapshot that hadn't
   *   caught up yet — which made the card briefly look "empty" and hide
   *   itself right when a fee line had just been added. Reusing the
   *   write's own response sidesteps that race entirely.
   */
  async #hydrate(writeAttributes, presetCart = null, options = {}) {
    try {
      this.#refreshMetafieldMaps();
      const cart = presetCart || (await (await fetch(route('cart_url', '/cart') + '.js')).json());
      if (!writeAttributes) {
        // Re-apply controls from saved attributes (covers morph child-replace).
        this.#renderControls();
        this.#applySavedState(cart.attributes || {});
      }
      await this.#evaluate(cart, !!writeAttributes, options);
    } catch (e) {
      console.error('[delivery-scheduler] hydrate failed', e);
    }
  }
}

if (!customElements.get('delivery-scheduler')) {
  customElements.define('delivery-scheduler', DeliveryScheduler);
}